"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { OtpInput } from "@/components/auth/otp-input";
import { GradientButton } from "@/components/layout/gradient-button";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useOtpTimer } from "@/hooks/use-otp-timer";

const OTP_COOLDOWN_SECONDS = 20;

interface OtpLoginProps {
  email: string;
  onSubmit: (email: string, otp: string) => any;
  onResend: (email: string) => any;
  onBack: () => void;
}

export function OtpLogin({ email, onSubmit, onResend, onBack }: OtpLoginProps) {
  const [otp, setOtp] = useState("");
  const { remainingSeconds, setRemainingSeconds } = useOtpTimer(email, OTP_COOLDOWN_SECONDS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSubmittedOtp = useRef("");

  const handleSubmit = useCallback(
    async (otpToSubmit: string) => {
      if (otpToSubmit.length !== 6 || isSubmitting) return;
      setIsSubmitting(true);
      lastSubmittedOtp.current = otpToSubmit;
      try {
        await onSubmit(email, otpToSubmit);
      } finally {
        setIsSubmitting(false);
      }
    },
    [email, onSubmit, isSubmitting]
  );

  // Auto-submit when OTP is complete
  useEffect(() => {
    if (otp.length < 6) {
      lastSubmittedOtp.current = "";
    }
    if (otp.length === 6 && !isSubmitting && otp !== lastSubmittedOtp.current) {
      handleSubmit(otp);
    }
  }, [otp, isSubmitting, handleSubmit]);

  return (
    <div className="w-full max-w-md mx-auto">
      {onBack && (
        <div className="flex justify-start mt-8">
          <Button
            type="button"
            onClick={onBack}
            variant="ghost"
            className="flex items-center gap-2 px-4 py-2 -ml-4 rounded-xl hover:bg-transparent cursor-pointer transition-colors text-xs font-medium"
            tabIndex={-1}
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </Button>
        </div>
      )}
      <div className="space-y-2 my-8">
        <h2 className="text-2xl font-bold">Enter OTP</h2>
        <p className="text-gray-600">
          We have sent a 6-digit code to{" "}
          <span className="font-semibold text-black">{email || "your email id"}</span>
        </p>
        <p className="text-sm text-gray-500 italic">Please check your inbox and spam folder.</p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(otp);
        }}
        className="my-8"
      >
        <OtpInput
          numDigits={6}
          value={otp}
          onChange={setOtp}
          resendInSecs={remainingSeconds}
          onResend={() => {
            onResend(email);
            setRemainingSeconds(OTP_COOLDOWN_SECONDS);
          }}
          autoFocus
          className="mb-8"
        />
        <GradientButton className="w-full" disabled={otp.length !== 6 || isSubmitting}>
          {isSubmitting ? "Logging in..." : "Login"}
        </GradientButton>
      </form>
    </div>
  );
}
